import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import {
    FaTwitter,
    FaLinkedinIn,
    FaGithub,
    FaInstagram,
    FaMediumM,
} from 'react-icons/fa';
import { AiOutlineSend, AiOutlineCheckCircle } from 'react-icons/ai';
import { FiPhone, FiAtSign } from 'react-icons/fi';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import Particle from '../Particle';
import { motion } from "framer-motion";

import { socialsData } from '../../data/socialsData';
import { contactsData } from '../../data/contactsData';
import './Connect.css';

function Connect() {
    const [showAlert, setShowAlert] = useState(false);
    const [alertVariant, setAlertVariant] = useState('danger');
    const [alertMessage, setAlertMessage] = useState('');

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');

    const [success, setSuccess] = useState(false);

    // Simple email validation function
    const isValidEmail = (email) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    const handleContactForm = (e) => {
        e.preventDefault();

        if (name && email && message) {
            if (isValidEmail(email)) {
                const responseData = {
                    name: name,
                    email: email,
                    message: message,
                };

                // Temporarily commenting out actual API call for debugging
                // axios.post(contactsData.sheetAPI, responseData)
                setSuccess(true);
                setAlertVariant('success');
                setAlertMessage('Message sent successfully!');
                setShowAlert(true);

                setName('');
                setEmail('');
                setMessage('');
                
                // Hide success message after 4 seconds
                setTimeout(() => {
                    setShowAlert(false);
                }, 4000);
            } else {
                setAlertVariant('danger');
                setAlertMessage('Invalid email');
                setShowAlert(true);
            }
        } else {
            setAlertVariant('danger');
            setAlertMessage('Please fill in all fields');
            setShowAlert(true);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="connect-section"
        >
            <Particle />
            <Container>
                <motion.div
                    className="connect-container"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                >
                    <div className="section-title text-center mb-5">
                        <motion.h1
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1 }}
                        >
                            Get In <span className="purple">Touch</span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 0.2 }}
                        >
                            Let's create something amazing together!
                        </motion.p>
                    </div>

                    <Row className="connect-content">
                        <Col lg={5} className="contact-info mb-4 mb-lg-0">
                            <motion.div
                                className="info-box"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.8 }}
                            >
                                <h3>Contact Information</h3>
                                <p>Feel free to reach out. I'm always open to discussing new projects, creative ideas, or opportunities to be part of your visions.</p>
                                
                                <div className="info-item">
                                    <div className="details-icon">
                                        <HiOutlineLocationMarker />
                                    </div>
                                    <div className="info-detail">
                                        <h6>Location</h6>
                                        <p>{contactsData.address}</p>
                                    </div>
                                </div>
                                
                                <div className="info-item">
                                    <div className="details-icon">
                                        <FiAtSign />
                                    </div>
                                    <div className="info-detail">
                                        <h6>Email</h6>
                                        <p>{contactsData.email}</p>
                                    </div>
                                </div>
                                
                                <div className="info-item">
                                    <div className="details-icon">
                                        <FiPhone />
                                    </div>
                                    <div className="info-detail">
                                        <h6>Phone</h6>
                                        <p>{contactsData.phone}</p>
                                    </div>
                                </div>

                                <div className="social-links">
                                    <h4>Follow Me</h4>
                                    <div className="social-icons">
                                        {Object.keys(socialsData).map((key) => (
                                            socialsData[key] && (
                                                <a
                                                    key={key}
                                                    href={socialsData[key]}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="social-icon"
                                                >
                                                    {getSocialIcon(key)}
                                                </a>
                                            )
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </Col>

                        <Col lg={7} className="contact-form">
                            <motion.div
                                className="form-box"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <h3>Send Me a Message</h3>
                                
                                {showAlert && (
                                    <Alert 
                                        variant={alertVariant} 
                                        onClose={() => setShowAlert(false)} 
                                        dismissible
                                    >
                                        {alertMessage}
                                    </Alert>
                                )}
                                
                                <Form onSubmit={handleContactForm}>
                                    <Form.Group className="mb-4" controlId="formName">
                                        <Form.Label>Your Name</Form.Label>
                                        <motion.input
                                            type="text"
                                            placeholder="Name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{
                                                duration: 0.5,
                                                delay: 0.2,
                                            }}
                                            className="form-control"
                                        />
                                    </Form.Group>
                                    
                                    <Form.Group className="mb-4" controlId="formEmail">
                                        <Form.Label>Your Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder="name@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                    
                                    <Form.Group className="mb-4" controlId="formMessage">
                                        <Form.Label>Message</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={5}
                                            placeholder="Type your message here..."
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                    
                                    <Button 
                                        variant="primary" 
                                        type="submit"
                                        className="submit-btn"
                                        disabled={success}
                                    >
                                        {!success ? 'Send Message' : 'Message Sent'}
                                    </Button>
                                </Form>
                            </motion.div>
                        </Col>
                    </Row>
                </motion.div>
            </Container>
        </motion.div>
    );
}

const getSocialIcon = (key) => {
    switch (key) {
        case 'github':
            return <FaGithub aria-label="GitHub" />;
        case 'linkedIn':
            return <FaLinkedinIn aria-label="LinkedIn" />;
        case 'twitter':
            return <FaTwitter aria-label="Twitter" />;
        case 'instagram':
            return <FaInstagram aria-label="Instagram" />;
        case 'medium':
            return <FaMediumM aria-label="Medium" />;
        default:
            return null;
    }
};

export default Connect;